import { RestartAlt } from "@mui/icons-material";
import { Alert, Box, Button, Card, CardContent, TextField, Typography } from "@mui/material";
import React, { JSX } from "react";
import { useTranslation } from "react-i18next";
import { fieldProps, useValidatedForm } from "../../../library/formValidation";
import { useApiFormError } from "../../../library/useApiFormError";
import { emailAdornment } from "../../../library/formsHelper";
import ResourcesIds from "../../../resources/ResourcesIds";
import { useReactMutation } from "../../../services/queryService";
import { services } from "../../../services/services";
import { ForgotPasswordRequest } from "../../../types";
import { cardContentStyles, cardStyles, pageWrapperStyles, signInButtonStyles, usernameFieldStyles } from "../../auth/loginForm.styles";
import { headerBoxStyles } from "../Dashboard/dashboard.styles";
import { createForgotPasswordSchema } from "./forgotPasswordSchema";

export const ForgotPassword = (): JSX.Element => {
    const { t: translate } = useTranslation();
    const schema = React.useMemo(() => createForgotPasswordSchema(translate), [translate]);
    const {
        register, handleSubmit, setError, formState: { errors, isValid, isSubmitting },
    } = useValidatedForm<ForgotPasswordRequest>(schema, { defaultValues: { emailAddress: '' } });

    const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
    const [emailAddress, setEmailAddress] = React.useState<string>('');

    const knownFields: string[] = React.useMemo(() => Object.keys(schema.fields), [schema]);
    // A bodyless 403 used to set the banner to `undefined` and say nothing at all.
    const { formError: error, report, clear } = useApiFormError<ForgotPasswordRequest>(
        setError, knownFields, translate, ResourcesIds.FORGOT_PASSWORD_ERROR);

    const forgotPasswordMutation = useReactMutation<void, Error, ForgotPasswordRequest>(services.authService.forgotPasswordMutation());

    const onSubmit = (data: ForgotPasswordRequest): void => {
        clear();

        forgotPasswordMutation.mutate(data, {
            onSuccess: () => {
                setEmailAddress(data.emailAddress);
                setIsSubmitted(true);
            },
            onError: report,
        });
    };

    return (
        <Box sx={pageWrapperStyles}>
            <Card sx={cardStyles}>
                <CardContent sx={cardContentStyles}>
                    {isSubmitted ?
                        <Box sx={headerBoxStyles}>
                            <Typography variant="h4" component="h1" gutterBottom>
                                {translate(ResourcesIds.EMAIL_SENT_TITLE)}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {translate(ResourcesIds.PASSWORD_RESET_EMAIL_INFO, { emailAddress })}
                            </Typography>
                            <Typography variant="body1" color="text.secondary">
                                {translate(ResourcesIds.PASSWORD_RESET_INSTRUCTIONS)}
                            </Typography>
                        </Box>
                        :
                        <>
                            <Box sx={headerBoxStyles}>
                                <Typography variant="h4" component="h1" gutterBottom>
                                    {translate(ResourcesIds.FORGOT_PASSWORD_TITLE)}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {translate(ResourcesIds.FORGOT_PASSWORD_SUBTITLE)}
                                </Typography>
                                <Typography variant="body1" color="text.secondary">
                                    {translate(ResourcesIds.FORGOT_PASSWORD_HINT)}
                                </Typography>
                            </Box>

                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                            <form onSubmit={handleSubmit(onSubmit)}>
                                <TextField
                                    {...register('emailAddress')}
                                    fullWidth
                                    label={translate(ResourcesIds.EMAIL_LABEL)}
                                    {...fieldProps(errors, 'emailAddress')}
                                    margin="normal"
                                    slotProps={{ input: { startAdornment: emailAdornment, } }}
                                    sx={usernameFieldStyles}
                                />


                                <Button
                                    type="submit"
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    disabled={!isValid || isSubmitting}
                                    startIcon={<RestartAlt />}
                                    sx={signInButtonStyles}
                                >
                                    {translate(isSubmitting ? ResourcesIds.RESETTING_PASSWORD : ResourcesIds.SUBMIT)}
                                </Button>
                            </form>
                        </>
                    }
                </CardContent>
            </Card>
        </Box>
    );
};